import { motion } from "framer-motion";
import type { Order } from "@/stores/businessStore";
import { cn } from "@/lib/cn";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

function formatETB(amount: number): string {
  return `${amount.toLocaleString("en-US")} ETB`;
}

export function PaymentsTab({ orders }: { orders: Order[] }) {
  const verifiedOrders = orders.filter((o) => o.paymentVerified);
  const pendingPayments = orders.filter((o) => !o.paymentVerified);

  return (
    <section>
      <div className="mb-3">
        <h2 className="font-headline text-base font-bold tracking-tight text-[var(--color-text)]">
          Payment Overview
        </h2>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm">
          <span className="text-xs text-[var(--color-text-secondary)]">Verified</span>
          <p className="font-headline text-lg font-bold text-emerald-600">
            {verifiedOrders.length}
          </p>
        </div>
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm">
          <span className="text-xs text-[var(--color-text-secondary)]">Pending</span>
          <p className="font-headline text-lg font-bold text-amber-600">
            {pendingPayments.length}
          </p>
        </div>
      </div>

      {pendingPayments.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          All payments verified. Nothing pending.
        </p>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-2"
        >
          {pendingPayments.map((order) => (
            <motion.div
              key={order.id}
              variants={fadeUp}
              className="flex items-center justify-between rounded-[var(--radius-lg)]
                         bg-[var(--color-surface)] p-3 shadow-sm"
            >
              <div>
                <span className="text-sm font-semibold text-[var(--color-text)]">
                  {order.contactName}
                </span>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {order.paymentMethod} &middot; {formatETB(order.totalPrice)}
                </p>
              </div>
              <span className="rounded-[var(--radius-full)] bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                Unverified
              </span>
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
}
